import { useDispatch, useSelector } from 'react-redux';
import differenceBy from 'lodash/differenceBy';
import Local from 'modules/Local';
import { addProductFormat, deleteProductFormat, getProductFormats } from 'modules/api/content';
import { addProductFormats, deleteProductFormats } from 'store/creative/reducer';
import { cancelFunctions } from 'utils/cancelFunctions';
import { CodeNameModel } from 'components/common/types';
import { Store } from 'components/common/types/Store.types';
import AsyncSelect from 'lib/AsyncSelect';
import { AsyncSelectHandler } from 'lib/AsyncSelect/AsyncSelect.types';
import usePendoTracking from 'customHooks/usePendoTracking';
import Chip, { Color, Width } from 'lib/Chip';

type ProductFormatsProps = {
  onTaggingRulesChange: () => Promise<void>;
  isDisabled?: boolean;
};

const ProductFormats: React.FC<ProductFormatsProps> = ({ isDisabled, onTaggingRulesChange }) => {
  const { getPendoDataId } = usePendoTracking();
  const dispatch = useDispatch();

  const marketId = useSelector((state: Store) => state.creative.marketId);
  const productFormat = useSelector((state: Store) => state.creative.productFormat);

  const onLoadLookupData = async (_dropdownName: string, query: string): Promise<CodeNameModel[]> => {
    try {
      const productFormatOptions: CodeNameModel[] = await getProductFormats(
        cancelFunctions,
        Local.getEnvironmentId(),
        query,
      );
      return differenceBy(productFormatOptions, productFormat, 'id');
    } catch (error) {
      return [];
    }
  };

  const onProductFormatChange: AsyncSelectHandler<CodeNameModel[]> = async (_, newValue) => {
    try {
      await addProductFormat(cancelFunctions, marketId, newValue[0].id);
      dispatch(addProductFormats(newValue[0]));
      onTaggingRulesChange();
      // eslint-disable-next-line no-empty
    } catch {}
  };

  const removeProductFormat = async (item: CodeNameModel): Promise<void> => {
    try {
      await deleteProductFormat(cancelFunctions, marketId, item.id);
      dispatch(deleteProductFormats(item.id ?? ''));
      onTaggingRulesChange();
      // eslint-disable-next-line no-empty
    } catch {}
  };

  return (
    <div>
      <div className={`${productFormat ? 'mb-3' : ''}`} data-test-id="product-format">
        <AsyncSelect
          dataTestId="product-format-input"
          name="productFormat"
          loadData={onLoadLookupData}
          selectedValue={null}
          placeholder="Select a product format"
          onSelect={onProductFormatChange}
          multiple
          isClearable={false}
          isMultiValueVisible={false}
          isDisabled={isDisabled}
          dataPendoId={getPendoDataId('Select Product Formats')}
        />
      </div>
      <div className="flex flex-wrap gap-x-7 gap-y-1" data-test-id="product-format-chips">
        {productFormat.length > 0 &&
          productFormat.map((product) => (
            <Chip
              key={product.id}
              label={product.name}
              isDisabled={isDisabled}
              onRemove={() => removeProductFormat(product)}
              dataTestId={`product-format-chip-${product.code}`}
              color={Color.PRIMARY}
              width={Width.FULL}
              truncateLabel={false}
            />
          ))}
      </div>
    </div>
  );
};

export default ProductFormats;
